<script setup lang="ts">
import type { ItemInfo } from "@/types"
import EditableText from "@/components/Controls/EditableText.vue"
import MatchStatus from "@/components/FileImport/Match/MatchStatus.vue"
import StatusDecoration from "@/components/FileImport/StatusDecoration.vue"
import { Decorator, ItemMatchStatus } from "@/types"
import { KDropdown } from "@kaleidoscope-solutions/ks-design-system"
import {
  ArrowDownIcon,
  CheckRingIcon,
  EditIcon,
} from "@kaleidoscope-solutions/ks-icons/vue/light"
import { computed, nextTick, ref } from "vue"

interface Props {
  /**
   * row content
   */
  item: ItemInfo
  /**
   * Options for selection
   */
  options: string[]
  /**
   * newly created items list
   */
  createdItems?: string[]
  /**
   * Allow the creation of dropdown items
   */
  allowCreate?: boolean
  /**
   * Show invalid decorator
   */
  showInvalid?: boolean
  /**
   * Disable dropdowns and input
   */
  disableDropdown?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  allowCreate: true,
  disableDropdown: false,
})

const emit = defineEmits<Emits>()

interface Emits {
  (event: "update", newValue: string): void
  (event: "create", newItemName: string): void
  (event: "ignore"): void
  (event: "rename", newItemName: string): void
}

const editable = ref(false)
const editRef = ref<HTMLElement>()

function handleUpdate(value: string | number | string[]) {
  emit("update", `${value ?? ""}`)
}

function handleIgnore(close: () => void) {
  emit("ignore")
  close()
}

function handleCreate(value: string, close: () => void) {
  emit("create", value)
  close()
}

const showDropdown = computed(() => !props.disableDropdown)

function labelFormatter(itemName: string) {
  if (
    props.item.status === ItemMatchStatus.ALREADY_IGNORED ||
    props.item.status === ItemMatchStatus.TO_IGNORE
  ) {
    return "-"
  }

  return itemName
}

function isCreated(itemName: string) {
  return props.createdItems ? props.createdItems?.includes(itemName) : false
}

function focusText() {
  nextTick(() => {
    if (editable.value && editRef.value) {
      window.getSelection()?.selectAllChildren(editRef.value)
    }
  })
}

function handleItemClick(event: Event) {
  if (editable.value) {
    event.stopPropagation()
    focusText()
  }
}

function updateEdit(value: string) {
  emit("rename", value)
  editable.value = false
}

function showCreateOption(suggestedName: string): boolean {
  if (!props.allowCreate) {
    return false
  }

  const regex = new RegExp(
    `^(?:${props.options
      // Escape pipe when in the option name.
      .map((option) => option.replace("|", "\\|"))
      .join("|")})$`,
    "i"
  )

  if (regex.test(suggestedName)) {
    return false
  }

  return suggestedName !== ""
}
</script>

<template>
  <td class="h-8 min-w-64 border border-gray-300 bg-warmgray-50 p-1 px-2">
    <div>{{ item.externalName }}</div>
  </td>
  <td class="h-8 min-w-64 border border-gray-300 p-0">
    <template v-if="showDropdown">
      <KDropdown
        :model-value="item.internalName"
        :items="options"
        :nullable="true"
        :placeholder="allowCreate ? 'Find or create' : 'Find'"
        class="h-full cursor-pointer ring-teal-700"
        @update:model-value="handleUpdate"
      >
        <template #selected="{ selected, open }">
          <div
            class="flex h-full w-full items-center px-2 py-0.5"
            :class="{ 'rounded-sm ring-2 ring-teal-700 ring-offset-1': open }"
          >
            <StatusDecoration
              v-if="showInvalid"
              :decorator="Decorator.INVALID"
              class="mr-1"
            />
            <div>
              {{ labelFormatter(selected as string) }}

              <sup
                v-if="item.status === ItemMatchStatus.CREATE"
                class="text-xs text-blue-500"
              >
                New
              </sup>
            </div>
            <ArrowDownIcon class="ml-auto h-4 w-4" />
          </div>
        </template>
        <template #item="{ item: option, selected }">
          <div
            class="flex items-center space-x-2"
            @click="handleItemClick"
          >
            <span
              v-if="isCreated(option.label)"
              class="text-blue-500"
            >
              New
            </span>

            <template v-if="selected === option.label">
              <EditableText
                v-model:editing="editable"
                :model-value="option.label"
                class="grow overflow-hidden text-ellipsis"
                :editable="true"
                @update:model-value="updateEdit"
              />

              <button
                v-if="isCreated(option.label)"
                type="button"
                class="text-black hover:opacity-50"
                @click.prevent="editable = !editable"
              >
                <EditIcon
                  v-if="!editable"
                  class="h-4 w-4"
                />
                <CheckRingIcon
                  v-if="editable"
                  class="h-4 w-4"
                />
              </button>
            </template>
            <div
              v-else
              class="overflow-hidden text-ellipsis"
            >
              {{ option.label }}
            </div>
          </div>
        </template>

        <template #buttons="{ close, suggestedName }">
          <button
            v-if="showCreateOption(suggestedName)"
            class="flex w-full cursor-pointer items-center border-t border-t-warmgray-400 px-3 py-2 font-bold text-violet-500 hover:bg-gray-300"
            @click="handleCreate(suggestedName, close)"
          >
            <span class="pl-2">Create "{{ suggestedName }}" & match</span>
          </button>
          <button
            class="flex w-full cursor-pointer items-center border-t border-t-warmgray-400 px-3 py-2 font-bold text-violet-500 hover:bg-gray-300"
            @click="handleIgnore(close)"
          >
            <span class="pl-2">Ignore</span>
          </button>
        </template>
      </KDropdown>
    </template>
    <div
      v-else
      class="flex h-full flex-1 items-center bg-warmgray-50 p-0.5 px-2"
    >
      <StatusDecoration
        v-if="showInvalid"
        :decorator="Decorator.INVALID"
        :colour-invert="true"
        class="text-gray-700"
      />
      {{ labelFormatter(item.internalName as string) }}
    </div>
  </td>
  <td class="h-8 min-w-32 border border-gray-300 bg-warmgray-50 p-1">
    <MatchStatus :status="item.status" />
  </td>
</template>
